import { debounce, first } from 'lodash';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IProductItem } from 'modules/shop/types/product';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { setPageLoading } from 'widgets/PageLoading';
import { cashierCtrl, InputSearchProductRef } from '../cashier.controller';
import { IUISelectItem } from '../cashier.interface';
import scanner_gif from './scanner.svg';
import search_icon from './search.svg';

const InputContainer = styled.div`
  position: relative;
  height: 56px;
  flex: 1;
  display: flex;
  align-items: center;
  border-radius: 50px;
  overflow: hidden;
  margin-left: 16px;
  margin-right: 8px;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 16px 40px 16px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  outline: none;
  border-radius: 50px;
  font-family: Poppins;
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  border: 3px solid #2D6772;
`;

const IconButton = styled.button`
  position: absolute;
  z-index: 99;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2D6772;
`;

export const inputSearchRef = React.createRef<HTMLInputElement>();

type Props = {
  value?: IUISelectItem[];
  onChange?: (value: IUISelectItem[]) => void;
};


const InputSearchProducts = forwardRef<InputSearchProductRef, Props>(({ }, ref) => {
  const [text, setText] = useState('');
  const forceStopScanner = useRef(false);
  const dispatch = useAppDispatch();
  const [isFocus, setFocus] = useState(false);

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!event.target) return;
      const input = event.target as HTMLInputElement;
      const code: string = input.value;
      if (!code?.trim()) return;
      input.value = '';
      setPageLoading(true);
      try {
        const res: IResponseData<IProductItem[]> = await cashierApis.getProductByCode(code);
        const product = first(res.data.data);
        if (product?.productCode === code) {
          cashierCtrl.chooseProduct(product);
        }
      } catch (error) {
      } finally {
        setPageLoading(false);
      }

      return;
    }
  };

  const _debounce = useCallback(
    debounce((textSearch: string) => {
      dispatch(cashierService.actions.async.searchProducts.fetch(textSearch));
    }, 600),
    []
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    forceStopScanner.current = true;
    setText(e.target.value);
    _debounce(e.target.value);
  };

  const clear = () => {
    _debounce('');
    setText('');
  };

  useImperativeHandle(ref, () => ({
    setForceStop: (value) => {
      forceStopScanner.current = value;
    }
  }));

  return (
    <>
      <InputContainer>
        <StyledInput
          value={text}
          onChange={onChange}
          type='text'
          placeholder='Search Products'
        />
        {isFocus ? <IconButton>
          <IconScanner>
            <img className='animation-pulse' src={scanner_gif} alt="scanner_gif" style={{ width: 40, height: 40 }} />
          </IconScanner>
        </IconButton> :
          <IconButton onClick={clear} type='button'>
            <img src={search_icon} alt="search_icon" width={32} height={32} style={{ width: 32, height: 32 }} />
          </IconButton>}
      </InputContainer>
      <input
        onBlur={() => setFocus(false)}
        onFocus={() => setFocus(true)} ref={inputSearchRef} onKeyDown={handleKeyDown} style={{ width: 0, opacity: 0 }} />
    </>
  );
});
InputSearchProducts.displayName = 'InputSearchProducts';
export default InputSearchProducts;

const IconScanner = styled.div`
`;
